import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { SpeakableSchema, BreadcrumbSchema } from '@/components/SchemaMarkup';
import { Calendar, ArrowRight } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import api from '@/utils/api';

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCanonical('/blog');
    setMeta('Workers Comp Blog | California Insurance Guide | CompareWorkersComp.com',
      'Expert articles on California workers compensation insurance. Learn about requirements, costs, class codes, and how to save on your premium.');
    api.get('/blog?limit=50&published_only=true').then(r => { setPosts(r.data.posts || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <>
      <SpeakableSchema name="Workers Comp Blog" url="https://compareworkerscomp.com/blog" />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Blog' }]} />

      <section className="relative pt-24 pb-12 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="speakable-intro text-white/30 text-xs mb-3">Workers compensation insurance articles and guides for California business owners. Expert content on compliance, class codes, costs, and coverage from CompareWorkersComp.com.</p>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 hero-text-shadow" data-testid="blog-h1">Workers Comp Blog</h1>
          <p className="text-white/60 hero-text-shadow-sm">Expert articles on California workers compensation insurance</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20"><div className="animate-spin w-8 h-8 border-4 border-cwc-gold border-t-transparent rounded-full mx-auto" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-500 py-20">No blog posts yet. Check back soon!</p>
          ) : (
            <div className="space-y-6" data-testid="blog-posts-list">
              {posts.map(post => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="block bg-gray-50 rounded-xl p-6 hover:bg-cwc-gold/5 border border-gray-100 transition-colors group" data-testid={`blog-card-${post.slug}`}>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <h2 className="text-lg font-bold text-cwc-navy group-hover:text-cwc-gold transition-colors mb-2">{post.title}</h2>
                  {post.meta_description && <p className="text-gray-500 text-sm">{post.meta_description}</p>}
                  <span className="text-cwc-gold text-sm font-medium mt-3 inline-flex items-center gap-1">Read More <ArrowRight className="w-3 h-3" /></span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
